import ld from 'lodash';
import Vue from 'vue';
import moment from 'moment';
import {
  HOME_CARDS,
  DATATABLE,
  TOTALE_MENSILE,
  CALENDAR_CHIUSURE,
  LOAD_FILTERS,
  ROW_DETAIL,
  ROW_DETAIL_COMANDA,
  VARIANTI_ARTICOLO,
  NUOVI_CLIENTI,
  TOP_ARTICOLI_OGGI,
  GRAFICO_CONTI,
  TORTA_TAVOLI,
  TORTA_SALE,
  VENDUTO_SU_ORDINATO,
  TOP_ARTICOLI,
  CORRISPETTIVI,
  RESET_STATS,
  ESPORTA_CORRISPETTIVI,
  DIFF_VENDUTO_ORDINATO,
  LOAD_MORE,
  FILTER_SEARCH,
} from '@/store/types';
import http from '@/http';
import { TIPI_DOCUMENTO } from '@/utils';

const filterDescr = {
  ragioneSociale: 'Ragione Sociale',
  cameriere: 'Cameriere',
  tavolo: 'Tavolo',
  sala: 'Sala',
  chiusura: 'Chiusura',
  tipoDocumento: 'Tipo Documento',
  gruppo: 'Gruppo',
  tag: 'Tag',
  pr: 'PR',
};

const filterMappers = {
  gruppo: (gruppi) => {
    const mapped = gruppi.reduce((c, n) => {
      const currKey = [];
      const currVal = [];
      [...n.matchAll(/\[(\w+)\]\s{2}([^[]+)(\s{0,3})/gm)].forEach((v) => {
        currKey.push(v[1]);
        currVal.push(v[0]);
        const k = currKey.join('.children.');
        if (ld.has(k)) {
          Vue.set(c, k, v[2].trim());
        } else {
          Vue.set(c, k, {
            text: v[2].trim(),
            value: currVal.join(''),
            children: {},
          });
        }
      });
      return c;
    }, {});
    const flattenSort = obj => ld.chain(obj).map(({ text, value, children }) => ({
      text,
      value,
      children: flattenSort(children),
    })).sortBy('text').value();
    const flatten = (obj, lvl = 0) => ld.chain(obj)
      .reduce((c, { text, value, children }) => [...c, {
        text,
        value,
        lvl,
      }, ...flatten(children, lvl + 1)], []).value();

    return flatten(flattenSort(mapped));
  },
  '*': data => data.map(val => ({
    text: val,
    value: val,
  })),
};

const filterCustomSlots = {
  gruppo: 'filtro-gruppo',
};

const getDefaultState = () => ({
  homeCards: {
    fiscale: 0,
    non_fiscale: 0,
    conti: 0,
    coperti: 0,
  },
  nuoviClienti: 0,
  topArticoliOggi: [],
  totaleMensile: [],
  datatables: {},
  graficoConti: [],
  tortaTavoli: [],
  tortaSale: [],
  vendutoSuOrdinato: [],
  topArticoli: [],
  diffVendutoOrdinato: {
    comande: 0,
    contiF: 0,
    contiNF: 0,
    offertiF: 0,
    offertiNF: 0,
    scontiF: 0,
    scontiNF: 0,
    storni: 0,
  },
  corrispettivi: {
    coperti: null,
    corrispettivi: [],
    corrispettiviDoc: [],
    pagamenti: [],
    tipiDoc: [],
    ultimaFattura: null,
    complessivo: 0,
  },
  tableFilters: {
    ragioneSociale: {
      loaded: false,
      data: [],
      multi: true,
      paginated: {
        size: 20,
        loadMore: true,
      },
      filterable: true,
    },
    cameriere: {
      loaded: false,
      data: [],
      multi: true,
      paginated: null,
      filterable: true,
    },
    tavolo: {
      loaded: false,
      data: [],
      multi: true,
      paginated: null,
      filterable: true,
    },
    sala: {
      loaded: false,
      data: [],
      multi: true,
      paginated: null,
      filterable: true,
    },
    gruppo: {
      loaded: false,
      data: [],
      multi: true,
      paginated: null,
      filterable: true,
    },
    tag: {
      loaded: false,
      data: [],
      multi: true,
      paginated: null,
      filterable: true,
    },
    pr: {
      loaded: false,
      data: [],
      multi: true,
      paginated: null,
      filterable: true,
    },
    chiusura: {
      loaded: true,
      data: [{
        text: 'Fiscale',
        value: 1,
      }, {
        text: 'Statistico',
        value: 2,
      }],
      multi: false,
      paginated: null,
      filterable: false,
    },
    tipoDocumento: {
      loaded: true,
      data: ld.map(TIPI_DOCUMENTO, (v, k) => ({ value: k, text: v })),
      multi: true,
      paginated: null,
      filterable: false,
    },
  },
});

const actions = {
  [HOME_CARDS]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/stat-oggi');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(HOME_CARDS, resp.data);
    }
  },
  [DATATABLE]: async ({ commit }, [name, source, settings]) => {
    let resp;
    try {
      resp = await http.post(`datatables/${source}`, settings);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(DATATABLE, [name, resp.data]);
    }
  },
  [TOTALE_MENSILE]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/totale-mensile');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(TOTALE_MENSILE, resp.data);
    }
  },
  [CALENDAR_CHIUSURE]: async (_, localCalendar = null) => {
    let resp;
    try {
      let params = null;
      if (localCalendar) {
        params = {
          headers: {
            calendarFrom: localCalendar.from,
            calendarTo: localCalendar.to,
          },
        };
      }
      resp = await http.get('chiusure/calendario', params);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      return resp.data;
    }
    return null;
  },
  [ROW_DETAIL]: async (_, [locale, id]) => {
    let resp;
    try {
      resp = await http.get(`conti/dettaglio/${locale}/${id}`);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      return resp.data;
    }
    return null;
  },
  [ROW_DETAIL_COMANDA]: async (_, [locale, id]) => {
    let resp;
    try {
      resp = await http.get(`comande/dettaglio/${locale}/${id}`);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      return resp.data;
    }
    return null;
  },
  [VARIANTI_ARTICOLO]: async (_, [locale, codArticolo]) => {
    let resp;
    try {
      resp = await http.get(`conti/varianti-articolo/${locale}/${codArticolo}`);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      return resp.data;
    }
    return null;
  },
  [LOAD_FILTERS]: async ({ state, commit }) => {
    const filtri = ld
      .chain(state.tableFilters)
      .pickBy(v => !v.loaded)
      .map((v, k) => [k, v.paginated && v.paginated.size])
      .value();
    if (filtri.length === 0) return;
    let resp;
    try {
      resp = await http.post('filtri', filtri);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(LOAD_FILTERS, resp.data);
    }
  },
  [LOAD_MORE]: async ({ state, commit }, [filtro, text]) => {
    let resp;
    try {
      resp = await http.post('filtri/load-more', [
        filtro,
        text,
        state.tableFilters[filtro].paginated.size,
        state.tableFilters[filtro].data.length,
      ]);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(LOAD_MORE, [filtro, resp.data]);
    }
  },
  [FILTER_SEARCH]: async ({ state, commit }, [filtro, text]) => {
    let resp;
    try {
      resp = await http.post('filtri/search', [
        filtro,
        text,
        state.tableFilters[filtro].paginated.size,
      ]);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(FILTER_SEARCH, [filtro, resp.data]);
    }
  },
  [NUOVI_CLIENTI]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/nuovi-clienti');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(NUOVI_CLIENTI, resp.data);
    }
  },
  [TOP_ARTICOLI_OGGI]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/top-articoli-oggi');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(TOP_ARTICOLI_OGGI, resp.data);
    }
  },
  [TOP_ARTICOLI]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/top-articoli');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(TOP_ARTICOLI, resp.data);
    }
  },
  [GRAFICO_CONTI]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/grafico-conti');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(GRAFICO_CONTI, resp.data);
    }
  },
  [TORTA_TAVOLI]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/torta-tavoli');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(TORTA_TAVOLI, resp.data);
    }
  },
  [TORTA_SALE]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/torta-sale');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(TORTA_SALE, resp.data);
    }
  },
  [VENDUTO_SU_ORDINATO]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/venduto-su-ordinato');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(VENDUTO_SU_ORDINATO, resp.data);
    }
  },
  [CORRISPETTIVI]: async ({ commit }, locale) => {
    let resp;
    try {
      resp = await http.get(`conti/corrispettivi/${locale}`);
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(CORRISPETTIVI, resp.data);
    }
  },
  [DIFF_VENDUTO_ORDINATO]: async ({ commit }) => {
    let resp;
    try {
      resp = await http.get('conti/diff-venduto-ordinato');
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      commit(DIFF_VENDUTO_ORDINATO, resp.data);
    }
  },
  [ESPORTA_CORRISPETTIVI]: async (_, locale) => {
    let resp;
    try {
      resp = await http.get(`conti/export-corrispettivi/${locale}`, {
        responseType: 'blob',
      });
    } catch (err) {
      resp = err;
    }
    if (resp.status === 200) {
      const filenameMatch = resp.headers['content-disposition'].match(/filename=(.*)/);
      let filename = 'export.pdf';
      if (filenameMatch.length > 1) {
        filename = filenameMatch[1]; //eslint-disable-line
      }
      return {
        result: true,
        blob: new Blob([resp.data], { type: 'application/pdf' }),
        filename,
      };
    }
    return {
      result: false,
      blob: resp,
      filename: null,
    };
  },
};

const mutations = {
  [HOME_CARDS]: (state, data) => {
    ld.merge(state.homeCards, data);
  },
  [DATATABLE]: (state, [name, { rows, totalRows, totals }]) => {
    if (!(name in state.datatables)) {
      state.datatables = { //eslint-disable-line
        [name]: {
          rows: [],
          totalRows: 0,
          totals: {},
        },
        ...state.datatables,
      };
    }
    Vue.set(state.datatables[name], 'rows', rows);
    Vue.set(state.datatables[name], 'totalRows', totalRows);
    Vue.set(state.datatables[name], 'totals', totals || {});
  },
  [TOTALE_MENSILE]: (state, data) => {
    Vue.set(state, 'totaleMensile', data);
  },
  [LOAD_FILTERS]: (state, data) => {
    ld.forEach(data, (v, k) => {
      Vue.set(state.tableFilters[k], 'data', k in filterMappers ? filterMappers[k](v) : filterMappers['*'](v));
      Vue.set(state.tableFilters[k], 'loaded', true);
    });
  },
  [LOAD_MORE]: (state, [k, v]) => {
    if (v.length === 0) {
      Vue.set(state.tableFilters[k].paginated, 'loadMore', false);
      return;
    }
    Vue.set(
      state.tableFilters[k],
      'data',
      [
        ...state.tableFilters[k].data,
        ...(k in filterMappers ? filterMappers[k](v) : filterMappers['*'](v)),
      ],
    );
  },
  [FILTER_SEARCH]: (state, [k, v]) => {
    Vue.set(
      state.tableFilters[k],
      'data',
      k in filterMappers ? filterMappers[k](v) : filterMappers['*'](v),
    );
    Vue.set(state.tableFilters[k].paginated, 'loadMore', v.length !== 0);
  },
  [NUOVI_CLIENTI]: (state, data) => {
    Vue.set(state, 'nuoviClienti', data);
  },
  [TOP_ARTICOLI_OGGI]: (state, data) => {
    Vue.set(state, 'topArticoliOggi', data);
  },
  [TOP_ARTICOLI]: (state, data) => {
    Vue.set(state, 'topArticoli', data);
  },
  [GRAFICO_CONTI]: (state, data) => {
    Vue.set(state, 'graficoConti', data);
  },
  [TORTA_TAVOLI]: (state, data) => {
    Vue.set(state, 'tortaTavoli', data);
  },
  [TORTA_SALE]: (state, data) => {
    Vue.set(state, 'tortaSale', data);
  },
  [VENDUTO_SU_ORDINATO]: (state, data) => {
    Vue.set(state, 'vendutoSuOrdinato', data);
  },
  [CORRISPETTIVI]: (state, data) => {
    Vue.set(state, 'corrispettivi', data);
  },
  [RESET_STATS]: (state) => {
    ld.assign(state, getDefaultState());
  },
  [DIFF_VENDUTO_ORDINATO]: (state, data) => {
    Vue.set(state, 'diffVendutoOrdinato', data);
  },
};

const getters = {
  datatable(state) {
    return name => (name in state.datatables ? state.datatables[name] : {
      rows: [],
      totalRows: 0,
      totals: {},
    });
  },
  totaleMensile(state) {
    return state.totaleMensile.map(tot => ({
      ...tot,
      nome: moment.months(tot.mese - 1),
    }));
  },
  filtri(state) {
    return ld.map(state.tableFilters, (v, k) => ({
      d: filterDescr[k],
      s: k in filterCustomSlots ? filterCustomSlots[k] : null,
      t: k,
      m: v.multi,
      p: !!v.paginated,
      lm: v.paginated && v.paginated.loadMore,
      data: v.loaded ? v.data : [],
      f: v.filterable,
    }));
  },
  corrispettivi(state) {
    const {
      coperti,
      corrispettivi,
      pagamenti: rowPagamenti,
      corrispettiviDoc: rowCorrispettiviDoc,
      tipiDoc,
      ultimaFattura,
      complessivo,
    } = state.corrispettivi;
    const pagamenti = ld(rowPagamenti)
      .groupBy('[0]')
      .map((v, k) => ({
        descrizione: k,
        pagamenti: ld.map(v, v1 => ({
          descrizione: v1[1],
          valore: v1[2],
          valore_gestionale: v1[3],
        })),
      })).value();
    const corrispettiviDoc = ld(rowCorrispettiviDoc)
      .groupBy('[0]')
      .map((v, k) => ({
        descrizione: k,
        corrispettivi: ld(v)
          .groupBy('[1]')
          .map((v1, k1) => ({
            descrizione: k1,
            pagamenti: ld.map(v1, p => ({
              descrizione: p[3],
              valore: p[4],
              valore_gestionale: p[5],
            })),
          })).value(),
      }))
      .value();

    return {
      coperti,
      corrispettivi,
      pagamenti,
      corrispettiviDoc,
      tipiDoc,
      ultimaFattura,
      complessivo,
    };
  },
};

const state = getDefaultState();

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
